import React, { useEffect, useState } from "react";
import SlidingPanel from "react-sliding-side-panel";
import "./ObjectiveDetailsSlidingPanel.scss";
import { Accordion } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { ReactSVG } from "react-svg";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getObjectiveDetails } from "../../_redux/BusinessImprovementPlanActions";
import { BoldText, LightText, NormalText, PrimaryBoldText, PrimarySmallNormalText, SmallBoldText, SmallLightText, SmallNormalText } from "../../../../../utils/typographyUtils";
import { ConditionalRender } from "../../../../shared/ConditionalRender/ConditionalRender";
import { SkeletonLoadingAnimation } from "../../../../../base/LoadingAnimation";
import ButtonIcon from "../../../../shared/formComponents/StandardButton/ButtonIcon";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

export const ObjectiveDetailsSlidingPanel = ({
  isOpen,
  objectiveId,
  onClose,
}) => {
  const dispatch = useDispatch();
  const {userId} = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [objectiveDetails, setObjectiveDetails] = useState({});
  const [isRecomExpanded, setIsRecomExpanded] = useState(true);
  const [isOkrExpanded, setIsOkrExpanded] = useState(true);
  useEffect(() => {
    handleGetObjectiveDetails();
  }, []);
  const handleGetObjectiveDetails = async () => {
    try {
      const resp = await dispatch(
        getObjectiveDetails(objectiveId, userId),
      );
      if (resp) {
        setObjectiveDetails(resp);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeadingSubHeadingAndContent = (heading, subHeading, content) => {
    return (
      <div className="p-3">
        <SmallBoldText classNames={"d-block"}>{heading}</SmallBoldText>
        <SmallNormalText classNames={"d-block mb-2"}>
          {subHeading}
        </SmallNormalText>
        <SmallNormalText>{content}</SmallNormalText>
      </div>
    );
  };

  const renderLabelAndData = (label, data) => {
    return (
      <div className={`d-flex`}>
        <SmallLightText classNames={"me-3"}>{label}</SmallLightText>
        <SmallNormalText>{data}</SmallNormalText>
      </div>
    );
  };
  return (
    <SlidingPanel
      type="right"
      isOpen={isOpen}
      size={40}
      panelContainerClassName="objective-details-panel-container rounded bg-white"
      backdropClicked={onClose}
    >
      <div>
        <div className="p-3 border-bottom">
          <div className="d-flex flex-column align-items-end ">
            <img
              onClick={onClose}
              src="/assets/images/close_icon.svg"
              className="cursor-pointer mb-2"
            />
          </div>
          <div>
            <NormalText>
              Here’s the context behind the objective, along with the linked
              X-Ray recommendation and the AI-suggested draft OKRs that helped
              shape the final version
            </NormalText>
          </div>
        </div>

        <ConditionalRender condition={isLoading}>
          <SkeletonLoadingAnimation height={45} />
          <SkeletonLoadingAnimation height={45} />
        </ConditionalRender>
        <ConditionalRender condition={!isLoading}>
          <Accordion defaultActiveKey={["0", "1"]} alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header
                onClick={() => setIsRecomExpanded(!isRecomExpanded)}
              >
                <div className="d-flex align-items-center w-100 justify-content-between">
                  <div>
                    <img
                      src="/assets/images/recommendation_context.svg"
                      className="mb-1 me-2"
                    />
                    <PrimarySmallNormalText classNames={"ms-2"}>
                      X-Ray Recommendation
                    </PrimarySmallNormalText>
                  </div>
                  <ButtonIcon
                    icon={isRecomExpanded ? ChevronUp : ChevronDown}
                    className="cursor-pointer"
                    color={"#08B3C3"}
                    onClick={() => setIsRecomExpanded(!isRecomExpanded)}
                  />
                </div>
              </Accordion.Header>
              <Accordion.Body className="bg-white p-0">
                <div className="px-3">
                  <img src="/assets/images/practice.svg" className="me-2" />
                  <BoldText classNames={"me-2"}>Practice</BoldText>
                  <NormalText>{objectiveDetails.practice}</NormalText>
                </div>
                <div className="p-3">
                  {objectiveDetails.discipline &&
                    renderLabelAndData(
                      "Discipline",
                      objectiveDetails.discipline ?? "-",
                    )}
                  {/* {objectiveDetails.sub_discipline &&
                    renderLabelAndData(
                      "Sub Discipline",
                      objectiveDetails.sub_discipline ?? "-",
                    )}
                  {objectiveDetails.sub_sub_discipline &&
                    renderLabelAndData(
                      "Sub Sub Discipline",
                      objectiveDetails.sub_sub_discipline ?? "-",
                    )} */}
                  {/* {objectiveDetails.rank &&
                  renderLabelAndData("Rank", objectiveDetails.rank ?? "-")} */}
                </div>
                <div className="bg-light-new px-3 py-2 d-flex justify-content-between align-items-center">
                  <div className="bg-white rounded d-flex px-3 py-1 align-items-center">
                    <SmallLightText classNames={"font-weight-bold"}>
                      Level
                    </SmallLightText>
                    <span className="vertical-separator-stretched mx-3"></span>
                    <SmallNormalText>
                      Current{" "}
                      <PrimaryBoldText>
                        {objectiveDetails?.current_level}
                      </PrimaryBoldText>
                    </SmallNormalText>
                    <ReactSVG
                      src="/assets/images/right_arrow_small.svg"
                      className="mx-3"
                    />
                    <SmallNormalText>
                      Next{" "}
                      <PrimaryBoldText>
                        {objectiveDetails?.next_level}
                      </PrimaryBoldText>
                    </SmallNormalText>
                  </div>
                  <div className="bg-white rounded px-3 py-1">
                    <img src="/assets/images/star.svg" className="me-2" />
                    <SmallLightText classNames={"me-3"}>
                      Highest Level
                    </SmallLightText>
                    <PrimaryBoldText>
                      {objectiveDetails?.highest_level}
                    </PrimaryBoldText>
                  </div>
                </div>
                <div>
                  {renderHeadingSubHeadingAndContent(
                    "Recommendation: ",
                    "Below is the recommendation to help you move to the next level and improve the practice",
                    objectiveDetails?.recommendation,
                  )}
                  {objectiveDetails?.coachNote &&
                    renderHeadingSubHeadingAndContent(
                      "Coach Note",
                      undefined,
                      objectiveDetails?.coachNote ?? "-",
                    )}
                </div>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header
                className="cursor-pointer"
                onClick={() => setIsOkrExpanded(!isOkrExpanded)}
              >
                <div className="d-flex align-items-center w-100 justify-content-between">
                  <div>
                    <img
                      src="/assets/images/sparkles.svg"
                      className="mb-1 me-2"
                    />
                    <PrimarySmallNormalText classNames={"ms-2"}>
                      AI suggested OKR
                    </PrimarySmallNormalText>
                  </div>
                  <ButtonIcon
                    icon={isOkrExpanded ? ChevronUp : ChevronDown}
                    className="cursor-pointer"
                    color={"#08B3C3"}
                    onClick={() => setIsOkrExpanded(!isOkrExpanded)}
                  />
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-0 bg-white">
                <div>
                  <div className="border-bottom p-3 pt-0">
                    <img src="/assets/images/objectives.svg" />
                    <SmallNormalText classNames={"ms-2"}>
                      Objective
                    </SmallNormalText>
                    <SmallLightText classNames={"ms-2"}>
                      {objectiveDetails.description}
                    </SmallLightText>
                  </div>
                  <div className="p-3 border-bottom">
                    <div className="d-flex align-items-center w-100">
                      <img
                        src={`/assets/images/key-results.svg`}
                        className="me-2"
                      />
                      <SmallBoldText>
                        Key Results{" "}
                        <LightText classNames={"font-weight-normal"}>
                          ({objectiveDetails?.key_results?.length})
                        </LightText>
                      </SmallBoldText>
                    </div>
                    <div className="px-3">
                      {objectiveDetails?.key_results?.map(
                        (keyResult, index) => {
                          return (
                            <div className="d-flex my-2 ms-2">
                              <SmallLightText classNames={"me-2"}>
                                {index + 1}
                              </SmallLightText>
                              <SmallLightText>
                                {keyResult.key_result}
                              </SmallLightText>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="d-flex align-items-center w-100">
                      <img
                        src={`/assets/images/initiatives.svg`}
                        className="me-2"
                      />
                      <SmallBoldText>
                        Initiatives{" "}
                        <LightText classNames={"font-weight-normal"}>
                          ({objectiveDetails?.initiatives?.length})
                        </LightText>
                      </SmallBoldText>
                    </div>
                    <div className="px-3">
                      {objectiveDetails?.initiatives?.map(
                        (initiative, index) => {
                          return (
                            <div className="d-flex my-2 ms-2">
                              <SmallLightText classNames={"me-2"}>
                                {index + 1}
                              </SmallLightText>
                              <SmallLightText>
                                {initiative.initiative}
                              </SmallLightText>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </ConditionalRender>
      </div>
    </SlidingPanel>
  );
};
